import { Add, DeleteOutline } from '@mui/icons-material';
import DynamicModal from 'components/DynamicModal';
import RemoveConfirmationDialog from 'components/RemoveConfirmationDialog';
import { useAtomValue } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import { Button, Form, FormGroup, Input, Label } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { getKPIErrorMessage } from './helper';
import styles from './styles.module.css';
import { KPISection, KPIUnit } from './types';

interface CreateCustomSectionModalProps {
  isOpen: boolean;
  maxItems: number;
  section?: KPISection | null;
  toggle: () => void;
  onSaved: () => void;
}

interface CustomMetricDraft {
  id: number;
  metricId?: number;
  label: string;
  unit: KPIUnit;
  goal: 'higher' | 'lower';
}

const createItem = (id: number): CustomMetricDraft => ({
  id,
  label: '',
  unit: 'count',
  goal: 'higher',
});

const CreateCustomSectionModal = ({
  isOpen,
  maxItems,
  section,
  toggle,
  onSaved,
}: CreateCustomSectionModalProps) => {
  const isEditing = Boolean(section);
  const formId = 'custom-kpi-form';
  const companyId = useAtomValue(companyIdAtom);
  const nextItemId = useRef(2);
  const [heading, setHeading] = useState('');
  const [items, setItems] = useState<CustomMetricDraft[]>(() => [
    createItem(1),
  ]);
  const [deletingItem, setDeletingItem] = useState<CustomMetricDraft | null>(
    null,
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setDeletingItem(null);
      return;
    }
    if (section) {
      nextItemId.current = section.metrics.length + 1;
      setHeading(section.label);
      setItems(
        section.metrics.map((metric, index) => ({
          id: index + 1,
          metricId: metric.id,
          label: metric.label,
          unit: metric.unit,
          goal: metric.goal || 'higher',
        })),
      );
      return;
    }
    nextItemId.current = 2;
    setHeading('');
    setItems([createItem(1)]);
  }, [isOpen, section]);

  const updateItem = (
    id: number,
    field: 'label' | 'unit' | 'goal',
    value: string,
  ) => {
    setItems((current) =>
      current.map((item) =>
        item.id === id ? { ...item, [field]: value } : item,
      ),
    );
  };

  const deleteItem = () => {
    if (!deletingItem) return;
    setItems((current) =>
      current.filter((candidate) => candidate.id !== deletingItem.id),
    );
    setDeletingItem(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedLabels = items.map((item) =>
      item.label.trim().toLocaleLowerCase(),
    );
    if (new Set(normalizedLabels).size !== normalizedLabels.length) {
      showFailureToast('KPI names must be unique within a KPI.');
      return;
    }

    setSaving(true);
    try {
      const endpoint = isEditing
        ? `kpi/custom-sections/${section?.id}/`
        : 'kpi/custom-sections/';
      await callApi(
        endpoint,
        isEditing ? 'PATCH' : 'POST',
        {},
        {
          company_id: companyId,
          label: heading.trim(),
          metrics: items.map(({ metricId, label, unit, goal }) => ({
            ...(metricId ? { id: metricId } : {}),
            label: label.trim(),
            unit,
            goal,
          })),
        },
        null,
        isEditing
          ? 'kpi/custom-sections/:kpi-custom-section-update'
          : 'kpi/custom-sections/:kpi-custom-section-create',
      );
      showSuccessToast(
        isEditing
          ? 'Custom KPI updated successfully.'
          : 'Custom KPI created successfully.',
      );
      onSaved();
      toggle();
    } catch (error) {
      showFailureToast(
        getKPIErrorMessage(
          error,
          isEditing
            ? 'Unable to update custom KPI.'
            : 'Unable to create custom KPI.',
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <Form className={styles.modalBody} id={formId} onSubmit={handleSubmit}>
      <FormGroup>
        <Label for="custom-kpi-heading">KPI heading</Label>
        <Input
          id="custom-kpi-heading"
          type="text"
          maxLength={100}
          value={heading}
          onChange={(event) => setHeading(event.target.value)}
          required
        />
      </FormGroup>

      <section className={styles.formSection}>
        {items.map((item, index) => (
          <div className={styles.customMetricRow} key={item.id}>
            <FormGroup>
              <Label for={`custom-kpi-item-${item.id}`}>KPI {index + 1}</Label>
              <Input
                id={`custom-kpi-item-${item.id}`}
                type="text"
                maxLength={120}
                value={item.label}
                onChange={(event) =>
                  updateItem(item.id, 'label', event.target.value)
                }
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for={`custom-kpi-unit-${item.id}`}>Unit</Label>
              <Input
                id={`custom-kpi-unit-${item.id}`}
                type="select"
                value={item.unit}
                onChange={(event) =>
                  updateItem(item.id, 'unit', event.target.value)
                }
              >
                <option value="count">Count</option>
                <option value="days">Days</option>
                <option value="percent">Percent</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for={`custom-kpi-goal-${item.id}`}>Goal</Label>
              <Input
                id={`custom-kpi-goal-${item.id}`}
                type="select"
                value={item.goal}
                onChange={(event) =>
                  updateItem(item.id, 'goal', event.target.value)
                }
              >
                <option value="higher">Higher is better</option>
                <option value="lower">Lower is better</option>
              </Input>
            </FormGroup>
            <Button
              className={styles.removeMetricButton}
              color="link"
              type="button"
              title="Delete KPI"
              aria-label={`Delete KPI ${index + 1}`}
              disabled={items.length === 1}
              onClick={() => setDeletingItem(item)}
            >
              <DeleteOutline fontSize="small" />
            </Button>
          </div>
        ))}
        <Button
          className={styles.addMetricButton}
          color="link"
          type="button"
          disabled={items.length >= maxItems}
          title={`Maximum of ${maxItems} KPIs per custom KPI`}
          onClick={() => {
            const item = createItem(nextItemId.current++);
            setItems((current) => [...current, item]);
          }}
        >
          <Add fontSize="small" />
          Add KPI
        </Button>
      </section>
    </Form>
  );
  const footerActions = (
    <>
      <Button
        color="secondary"
        type="button"
        onClick={toggle}
        disabled={saving}
      >
        Cancel
      </Button>
      {saving ? (
        <CircleSpinner size={20} color="#0665a9" loading={true} />
      ) : (
        <Button color="primary" type="submit" form={formId}>
          {isEditing ? 'Save KPI' : 'Create KPI'}
        </Button>
      )}
    </>
  );

  return (
    <>
      <DynamicModal
        isOpen={isOpen && !deletingItem}
        toggle={toggle}
        title={isEditing ? 'Edit Custom KPI' : 'Create Custom KPI'}
        content={content}
        footerActions={footerActions}
        size="lg"
      />
      <RemoveConfirmationDialog
        isOpen={Boolean(deletingItem)}
        toggle={() => setDeletingItem(null)}
        removeConfirmationTitle="Delete KPI"
        removeConfirmationMessage={
          deletingItem?.metricId
            ? `Permanently delete "${deletingItem.label}" and all of its historical values when this custom KPI is saved? This action cannot be undone.`
            : `Delete "${deletingItem?.label || 'this KPI'}" from this custom KPI?`
        }
        onRemove={deleteItem}
      />
    </>
  );
};

export default CreateCustomSectionModal;
